import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { toast } from "sonner";
import { Clock, Save, Loader2, Plus, Trash2, ChevronDown, ChevronRight, ArrowRight, Copy } from "lucide-react";
import { format } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ExportToItineraryDialog } from "./ExportToItineraryDialog";
import { ParticipantOptionSelector } from "./ParticipantOptionSelector";

interface CellData {
  value: string | number;
  formula?: string;
}

interface RowData {
  description: CellData;
  quantity: CellData;
  netCost: CellData;
  markupPercent: CellData;
  customPercent: CellData;
  sellPrice: CellData;
  notes: CellData;
}

interface SectionData {
  id: string;
  title: string;
  rows: RowData[];
  isOpen?: boolean;
}

import { Session } from "@supabase/supabase-js";

interface CostingsSpreadsheetProps {
  leadId: string;
  leadStartDate?: string | null;
  session: Session | null;
  paxCount?: number;
}

// Preview pax count state for option switching

const defaultRow = (): RowData => ({
  description: { value: "" },
  quantity: { value: 1 },
  netCost: { value: 0 },
  markupPercent: { value: 20 },
  customPercent: { value: 0 },
  sellPrice: { value: 0 },
  notes: { value: "" },
});

const calculateSellPrice = (netCost: number, quantity: number, markupPercent: number, customPercent: number): number => {
  // Step 1: Calculate total net cost
  const totalNetCost = netCost * (quantity || 0);
  
  // Step 2: Apply markup percentage (proper markup formula: cost × (1 + markup%))
  const baseSell = totalNetCost * (1 + markupPercent / 100);
  
  // Step 3: Apply custom percentage (VAT, levies, fees)
  const finalSell = baseSell * (1 + customPercent / 100);
  
  return Math.round(finalSell * 100) / 100;
};

export function CostingsSpreadsheet({ leadId, leadStartDate, session, paxCount = 1 }: CostingsSpreadsheetProps) {
  const queryClient = useQueryClient();
  const [sections, setSections] = useState<SectionData[]>([
    { id: crypto.randomUUID(), title: "General", rows: [defaultRow()], isOpen: true }
  ]);
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "unsaved">("saved");
  const [showHistory, setShowHistory] = useState(false);
  const [allExpanded, setAllExpanded] = useState(true);
  const [addedToItinerary, setAddedToItinerary] = useState<Set<string>>(new Set());
  const [exportingSection, setExportingSection] = useState<SectionData | null>(null);
  const [previewPaxCount, setPreviewPaxCount] = useState<number | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const effectivePaxCount = previewPaxCount || paxCount;

  const { data: costing, isLoading } = useQuery({
    queryKey: ["costing", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("itinerary_costings")
        .select("*, profiles!itinerary_costings_last_edited_by_fkey(full_name)")
        .eq("lead_id", leadId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const { data: auditLogs } = useQuery({
    queryKey: ["costing-audit-logs", costing?.id],
    queryFn: async () => {
      if (!costing?.id) return [];
      
      const { data, error } = await supabase
        .from("costing_audit_logs")
        .select("*, profiles!costing_audit_logs_user_id_fkey(full_name)")
        .eq("costing_id", costing.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!costing?.id,
  });

  useEffect(() => {
    if (costing?.grid_data) {
      const data = costing.grid_data as any;
      
      // Check if data is in new sectioned format or old flat format
      if (Array.isArray(data) && data.length > 0 && 'id' in data[0] && 'title' in data[0] && 'rows' in data[0]) {
        // New format - array of sections
        const loadedSections = (data as SectionData[]).map(section => ({
          ...section,
          isOpen: section.isOpen !== false,
          rows: section.rows.map(row => {
            const netCost = row.netCost?.value as number || 0;
            const quantity = row.quantity?.value as number || 1;
            const markupPercent = row.markupPercent?.value as number || 0;
            const customPercent = row.customPercent?.value as number || 0;
            
            // Recalculate sell price with corrected formula
            const recalculatedSellPrice = calculateSellPrice(netCost, quantity, markupPercent, customPercent);
            
            return {
              ...row,
              quantity: row.quantity || { value: 1 },
              customPercent: row.customPercent || { value: 0 },
              sellPrice: { value: recalculatedSellPrice }
            };
          })
        }));
        setSections(loadedSections.length > 0 ? loadedSections : [
          { id: crypto.randomUUID(), title: "General", rows: [defaultRow()], isOpen: true }
        ]);
      } else if (Array.isArray(data)) {
        // Old format - flat array of rows, migrate to single section
        const migratedRows = (data as RowData[]).map(row => {
          const netCost = row.netCost?.value as number || 0;
          const quantity = row.quantity?.value as number || 1;
          const markupPercent = row.markupPercent?.value as number || 0;
          const customPercent = row.customPercent?.value as number || 0;
          
          // Recalculate sell price with corrected formula
          const recalculatedSellPrice = calculateSellPrice(netCost, quantity, markupPercent, customPercent);
          
          return {
            ...row,
            quantity: row.quantity || { value: 1 },
            customPercent: row.customPercent || { value: 0 },
            sellPrice: { value: recalculatedSellPrice }
          };
        });
        setSections([{
          id: crypto.randomUUID(),
          title: "General",
          rows: migratedRows.length > 0 ? migratedRows : [defaultRow()],
          isOpen: true
        }]);
      }
    }
  }, [costing]);

  const saveMutation = useMutation({
    mutationFn: async ({ newSections, oldSections }: { newSections: SectionData[]; oldSections: SectionData[] }) => {
      // Fetch fresh session to avoid race conditions with prop timing
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.user?.id) throw new Error("Not authenticated");

      const changes: string[] = [];
      
      // Detect changes across all sections
      newSections.forEach((newSection, sectionIdx) => {
        const oldSection = oldSections[sectionIdx];
        
        // Check for new sections
        if (!oldSection) {
          changes.push(`Added section "${newSection.title}"`);
          return;
        }
        
        // Check for section title changes
        if (newSection.title !== oldSection.title) {
          changes.push(`Renamed section from "${oldSection.title}" to "${newSection.title}"`);
        }
        
        // Check for row changes within each section
        newSection.rows.forEach((newRow, idx) => {
          const oldRow = oldSection.rows[idx];
          if (!oldRow) {
            changes.push(`Added row ${idx + 1} in section "${newSection.title}"`);
            return;
          }

          if (newRow.description.value !== oldRow.description.value) {
            changes.push(`Changed Row ${idx + 1} Description in "${newSection.title}" from "${oldRow.description.value}" to "${newRow.description.value}"`);
          }
          if (newRow.quantity.value !== oldRow.quantity?.value) {
            changes.push(`Changed Row ${idx + 1} (${newRow.description.value || 'Unnamed'}) Quantity in "${newSection.title}" from ${oldRow.quantity?.value || 1} to ${newRow.quantity.value}`);
          }
          if (newRow.netCost.value !== oldRow.netCost.value) {
            changes.push(`Changed Row ${idx + 1} (${newRow.description.value || 'Unnamed'}) Net Cost in "${newSection.title}" from ${oldRow.netCost.value} to ${newRow.netCost.value}`);
          }
          if (newRow.markupPercent.value !== oldRow.markupPercent.value) {
            changes.push(`Changed Row ${idx + 1} (${newRow.description.value || 'Unnamed'}) Markup % in "${newSection.title}" from ${oldRow.markupPercent.value} to ${newRow.markupPercent.value}`);
          }
          if (newRow.customPercent?.value !== oldRow.customPercent?.value) {
            changes.push(`Changed Row ${idx + 1} (${newRow.description.value || 'Unnamed'}) Custom % in "${newSection.title}" from ${oldRow.customPercent?.value || 0} to ${newRow.customPercent?.value || 0}`);
          }
          if (newRow.notes.value !== oldRow.notes.value) {
            changes.push(`Changed Row ${idx + 1} (${newRow.description.value || 'Unnamed'}) Notes in "${newSection.title}"`);
          }
        });
      });
      
      // Check for deleted sections
      oldSections.forEach((oldSection, idx) => {
        if (!newSections[idx]) {
          changes.push(`Deleted section "${oldSection.title}"`);
        }
      });

      // Save to database
      if (costing?.id) {
        // Update existing
        const { error } = await supabase
          .from("itinerary_costings")
          .update({
            grid_data: newSections as unknown as any,
            last_edited_by: currentSession.user.id,
          })
          .eq("id", costing.id);

        if (error) throw error;

        // Log changes
        if (changes.length > 0) {
          for (const change of changes) {
            await supabase.from("costing_audit_logs").insert({
              costing_id: costing.id,
              user_id: currentSession.user.id,
              change_description: change,
            });
          }
        }
      } else {
        // Create new
        const { data: newCosting, error } = await supabase
          .from("itinerary_costings")
          .insert({
            lead_id: leadId,
            grid_data: newSections as unknown as any,
            last_edited_by: currentSession.user.id,
          })
          .select()
          .single();

        if (error) throw error;

        // Log creation
        await supabase.from("costing_audit_logs").insert({
          costing_id: newCosting.id,
          user_id: currentSession.user.id,
          change_description: "Created costings spreadsheet",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["costing", leadId] });
      queryClient.invalidateQueries({ queryKey: ["costing-audit-logs"] });
      setSaveStatus("saved");
    },
    onError: (error) => {
      console.error("Save error:", error);
      toast.error("Failed to save costings");
      setSaveStatus("unsaved");
    },
  });

  const debouncedSave = useCallback((newSections: SectionData[], oldSections: SectionData[]) => {
    setSaveStatus("saving");
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveMutation.mutate({ newSections, oldSections });
    }, 1000);
  }, [saveMutation]);

  const updateCell = (sectionId: string, rowIdx: number, field: keyof RowData, value: string | number) => {
    const oldSections = JSON.parse(JSON.stringify(sections));
    const newSections = [...sections];
    const sectionIdx = newSections.findIndex(s => s.id === sectionId);
    
    if (sectionIdx === -1) return;
    
    if (field === "netCost" || field === "markupPercent" || field === "quantity" || field === "customPercent") {
      const numValue = typeof value === "string" ? parseFloat(value) || 0 : value;
      newSections[sectionIdx].rows[rowIdx][field] = { value: numValue };
      
      // Auto-calculate sell price
      const row = newSections[sectionIdx].rows[rowIdx];
      const netCost = field === "netCost" ? numValue : (row.netCost.value as number);
      const quantity = field === "quantity" ? numValue : (row.quantity.value as number);
      const markupPercent = field === "markupPercent" ? numValue : (row.markupPercent.value as number);
      const customPercent = field === "customPercent" ? numValue : (row.customPercent?.value as number || 0);
      newSections[sectionIdx].rows[rowIdx].sellPrice = { value: calculateSellPrice(netCost, quantity, markupPercent, customPercent) };
    } else {
      newSections[sectionIdx].rows[rowIdx][field] = { value };
    }

    setSections(newSections);
    debouncedSave(newSections, oldSections);
  };

  const updateSectionTitle = (sectionId: string, newTitle: string) => {
    const oldSections = JSON.parse(JSON.stringify(sections));
    const newSections = sections.map(section =>
      section.id === sectionId ? { ...section, title: newTitle } : section
    );
    setSections(newSections);
    debouncedSave(newSections, oldSections);
  };

  const addSection = () => {
    const newSection: SectionData = {
      id: crypto.randomUUID(),
      title: `Section ${sections.length + 1}`,
      rows: [defaultRow()],
      isOpen: true
    };
    setSections([...sections, newSection]);
  };

  const deleteSection = (sectionId: string) => {
    if (sections.length === 1) {
      toast.error("Cannot delete the last section");
      return;
    }
    const oldSections = JSON.parse(JSON.stringify(sections));
    const newSections = sections.filter(s => s.id !== sectionId);
    setSections(newSections);
    debouncedSave(newSections, oldSections);
  };

  const duplicateSection = (sectionId: string) => {
    const sectionToDuplicate = sections.find(s => s.id === sectionId);
    if (!sectionToDuplicate) return;
    
    const oldSections = JSON.parse(JSON.stringify(sections));
    
    // Deep clone the section with new IDs
    const duplicatedSection: SectionData = {
      id: crypto.randomUUID(),
      title: `${sectionToDuplicate.title} (Copy)`,
      rows: sectionToDuplicate.rows.map(row => ({
        description: { value: row.description.value },
        quantity: { value: row.quantity.value },
        netCost: { value: row.netCost.value },
        markupPercent: { value: row.markupPercent.value },
        customPercent: { value: row.customPercent?.value || 0 },
        sellPrice: { value: row.sellPrice.value },
        notes: { value: row.notes.value },
      })),
      isOpen: true
    };
    
    // Insert the duplicate right after the original section
    const sectionIdx = sections.findIndex(s => s.id === sectionId);
    const newSections = [
      ...sections.slice(0, sectionIdx + 1),
      duplicatedSection,
      ...sections.slice(sectionIdx + 1)
    ];
    
    setSections(newSections);
    debouncedSave(newSections, oldSections);
    toast.success(`Duplicated "${sectionToDuplicate.title}"`);
  };

  const addRow = (sectionId: string) => {
    const newSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, rows: [...section.rows, defaultRow()] }
        : section
    );
    setSections(newSections);
  };

  const deleteRow = (sectionId: string, rowIdx: number) => {
    const section = sections.find(s => s.id === sectionId);
    if (section && section.rows.length === 1) {
      toast.error("Cannot delete the last row in a section");
      return;
    }
    const oldSections = JSON.parse(JSON.stringify(sections));
    const newSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, rows: section.rows.filter((_, i) => i !== rowIdx) }
        : section
    );
    setSections(newSections);
    debouncedSave(newSections, oldSections);
  };

  const toggleSection = (sectionId: string) => {
    setSections(sections.map(section =>
      section.id === sectionId ? { ...section, isOpen: !section.isOpen } : section
    ));
  };

  const toggleAllSections = () => {
    const newState = !allExpanded;
    setSections(sections.map(section => ({ ...section, isOpen: newState })));
    setAllExpanded(newState);
  };

  const getSectionTotal = (sectionRows: RowData[]) => {
    return sectionRows.reduce((sum, row) => sum + (row.sellPrice.value as number || 0), 0);
  };

  const getSectionNetCost = (sectionRows: RowData[]) => {
    return sectionRows.reduce((sum, row) => sum + ((row.netCost.value as number || 0) * (row.quantity.value as number || 0)), 0);
  };

  const grandTotal = sections.reduce((sum, section) => sum + getSectionTotal(section.rows), 0);
  const grandNetCost = sections.reduce((sum, section) => sum + getSectionNetCost(section.rows), 0);
  const grandProfit = grandTotal - grandNetCost;
  const grandMarginPct = grandTotal > 0 ? (grandProfit / grandTotal) * 100 : 0;

  const addToItinerary = useMutation({
    mutationFn: async (data: {
      title: string;
      description: string;
      price: number;
      iconId: string;
      sectionId: string;
      date?: string;
    }) => {
      // Fetch fresh session to avoid race conditions with prop timing
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession?.user?.id) throw new Error("Not authenticated");

      // Calculate next sort_order instead of hardcoding 999
      const { data: maxOrderData } = await supabase
        .from("itinerary_items")
        .select("sort_order")
        .eq("lead_id", leadId)
        .order("sort_order", { ascending: false, nullsFirst: false })
        .limit(1)
        .single();

      const nextSortOrder = (maxOrderData?.sort_order ?? -1) + 1;

      const payload = {
        lead_id: leadId,
        user_id: currentSession.user.id,
        item_type: "custom" as any,
        title: data.title,
        custom_title: data.title,
        custom_description: data.description,
        manual_price: data.price,
        manual_image_url: `default-${data.iconId}`,
        linked_costing_section_id: data.sectionId,
        date: data.date || null,
        sort_order: nextSortOrder,
      };

      console.log('💾 Inserting Itinerary Item to Database:', payload);

      // Create itinerary item with user-edited data
      const { error } = await supabase
        .from("itinerary_items")
        .insert(payload);

      if (error) {
        console.error('❌ Database Insert Error:', error);
        throw error;
      }

      console.log('✅ Successfully created itinerary item');
      return data;
    },
    onSuccess: (data) => {
      const formattedAmount = data.price.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
      toast.success(`Added "${data.title}" to Itinerary with a cost of $${formattedAmount}`);
      setAddedToItinerary(prev => new Set(prev).add(data.sectionId));
      setExportingSection(null);
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
    },
    onError: (error) => {
      console.error("Add to itinerary error:", error);
      toast.error("Failed to add to itinerary");
    },
  });

  const handleExportConfirm = (data: {
    title: string;
    description: string;
    price: number;
    iconId: string;
    date?: string;
  }) => {
    if (!exportingSection) return;
    
    console.log('📤 Handling Export Confirm with data:', data);
    
    addToItinerary.mutate({
      ...data,
      sectionId: exportingSection.id,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Costings & Calculations</h2>
            {costing && (
              <p className="text-sm text-muted-foreground mt-1">
                Last edited by {costing.profiles?.full_name || "Unknown"} at{" "}
                {format(new Date(costing.updated_at), "MMM d, yyyy HH:mm")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ParticipantOptionSelector
              leadId={leadId}
              selectedOptionId={null}
              onSelect={(_id, qty) => setPreviewPaxCount(qty)}
              variant="chips"
              autoSelectSingle={false}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={toggleAllSections}
            >
              {allExpanded ? "Collapse All" : "Expand All"}
            </Button>
            
            <div className="flex items-center gap-2">
              {saveStatus === "saving" && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Saving...</span>
                </>
              )}
              {saveStatus === "saved" && (
                <>
                  <Save className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-500">All changes saved</span>
                </>
              )}
            </div>
            
            {auditLogs && auditLogs.length > 0 && (
              <Sheet open={showHistory} onOpenChange={setShowHistory}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Clock className="h-4 w-4 mr-2" />
                    History
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Change History</SheetTitle>
                    <SheetDescription>
                      Track all changes made to this costings sheet
                    </SheetDescription>
                  </SheetHeader>
                  <ScrollArea className="h-[calc(100vh-180px)] mt-4">
                    <div className="space-y-4">
                      {auditLogs.map((log) => (
                        <div key={log.id} className="border-b pb-4">
                          <p className="text-sm font-medium">{log.change_description}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {log.profiles?.full_name || "Unknown"} •{" "}
                            {format(new Date(log.created_at), "MMM d, yyyy HH:mm")}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </Card>

      {/* Sections */}
      <div className="space-y-4">
        {sections.map((section, sectionIdx) => (
          <Collapsible
            key={section.id}
            open={section.isOpen}
            onOpenChange={() => toggleSection(section.id)}
          >
            <Card>
              <CollapsibleTrigger className="w-full">
                <div className="flex flex-wrap items-center justify-between gap-2 p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    {section.isOpen ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronRight className="h-5 w-5" />
                    )}
                    <Input
                      value={section.title}
                      onChange={(e) => {
                        e.stopPropagation();
                        updateSectionTitle(section.id, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 font-semibold max-w-xs"
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground mr-4">
                      Subtotal: {getSectionTotal(section.rows).toFixed(2)}
                    </span>
                    <Button
                      variant={addedToItinerary.has(section.id) ? "outline" : "default"}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!addedToItinerary.has(section.id)) {
                          setExportingSection(section);
                        }
                      }}
                      disabled={addedToItinerary.has(section.id)}
                    >
                      {addedToItinerary.has(section.id) ? (
                        <>Added</>
                      ) : (
                        <>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          Add to Itinerary
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateSection(section.id);
                      }}
                      title="Duplicate section"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSection(section.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="p-4 pt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="p-2 text-left font-semibold bg-muted">#</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[200px]">Item Description</th>
                          <th className="p-2 text-left font-semibold bg-muted w-20">Quantity</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[120px]">Net Cost</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[120px]">Subtotal</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[100px]">Markup %</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[100px]">Custom %</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[120px]">Sell Price</th>
                          <th className="p-2 text-left font-semibold bg-muted min-w-[200px]">Notes</th>
                          <th className="p-2 text-left font-semibold bg-muted w-[80px]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className="border-b hover:bg-muted/50">
                            <td className="p-2 text-muted-foreground">{rowIdx + 1}</td>
                            <td className="p-2">
                              <Input
                                value={row.description.value}
                                onChange={(e) => updateCell(section.id, rowIdx, "description", e.target.value)}
                                className="h-9"
                                placeholder="Enter item description"
                              />
                            </td>
                            <td className="p-2">
                              <NumericInput
                                value={row.quantity.value as number}
                                onChange={(val) => updateCell(section.id, rowIdx, "quantity", val)}
                                className="h-9 w-20"
                                step="1"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <NumericInput
                                value={row.netCost.value as number}
                                onChange={(val) => updateCell(section.id, rowIdx, "netCost", val)}
                                className="h-9"
                                step="0.01"
                              />
                            </td>
                            <td className="p-2">
                              <div className="h-9 px-3 py-2 rounded-md border bg-accent/10 font-medium">
                                {((row.netCost.value as number || 0) * (row.quantity.value as number || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </td>
                            <td className="p-2">
                              <NumericInput
                                value={row.markupPercent.value as number}
                                onChange={(val) => updateCell(section.id, rowIdx, "markupPercent", val)}
                                className="h-9"
                                step="1"
                                max="99"
                              />
                            </td>
                            <td className="p-2">
                              <NumericInput
                                value={(row.customPercent?.value as number) || 0}
                                onChange={(val) => updateCell(section.id, rowIdx, "customPercent", val)}
                                className="h-9"
                                step="0.1"
                                placeholder="0"
                              />
                            </td>
                            <td className="p-2">
                              <div className="h-9 px-3 py-2 rounded-md border bg-accent/10 font-medium">
                                {(row.sellPrice.value as number).toFixed(2)}
                              </div>
                            </td>
                            <td className="p-2">
                              <Input
                                value={row.notes.value}
                                onChange={(e) => updateCell(section.id, rowIdx, "notes", e.target.value)}
                                className="h-9"
                                placeholder="Optional notes"
                              />
                            </td>
                            <td className="p-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteRow(section.id, rowIdx)}
                                className="h-9"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <Button onClick={() => addRow(section.id)} className="mt-4" variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Row
                  </Button>

                  {/* Calculations Summary Row */}
                  {(() => {
                    const sectionSell = getSectionTotal(section.rows);
                    const sectionCost = getSectionNetCost(section.rows);
                    const sectionProfit = sectionSell - sectionCost;
                    const sectionMarginPct = sectionSell > 0 ? (sectionProfit / sectionSell) * 100 : 0;
                    const perPerson = sectionSell / (effectivePaxCount || 1);
                    return (
                      <div className="mt-3 rounded-md bg-muted/50 border px-4 py-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                        <span><span className="text-muted-foreground">Subtotal:</span> <span className="font-medium">{sectionSell.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                        <span><span className="text-muted-foreground">Cost:</span> <span className="font-medium">{sectionCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                        <span><span className="text-muted-foreground">Margin:</span> <span className="font-medium">{sectionProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({sectionMarginPct.toFixed(1)}%)</span></span>
                        <span><span className="text-muted-foreground">Per Person:</span> <span className="font-medium">{perPerson.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                      </div>
                    );
                  })()}
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        ))}
      </div>

      <Button onClick={addSection} variant="outline" className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        Add New Costing Group
      </Button>

      {/* Grand Total - Sticky Footer */}
      <Card className="sticky bottom-4 bg-background shadow-lg">
        <div className="p-4 flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold">Grand Total (All Sections)</span>
            <span className="text-2xl font-bold">{grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Total Cost: {grandNetCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span>Profit: {grandProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({grandMarginPct.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Per Person: {(grandTotal / (effectivePaxCount || 1)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </Card>

      {/* Export to Itinerary Dialog */}
      <ExportToItineraryDialog
        open={!!exportingSection}
        onOpenChange={(open) => !open && setExportingSection(null)}
        section={exportingSection}
        sectionTotal={exportingSection ? getSectionTotal(exportingSection.rows) : 0}
        leadStartDate={leadStartDate}
        onConfirm={handleExportConfirm}
        isLoading={addToItinerary.isPending}
      />
    </div>
  );
}
